/**
 * Privacy policy (§15).
 *
 * The published counterpart of `docs/privacy-policy.md`. The two are the same
 * document and must be changed together; a substantive change also means bumping
 * `POLICY_VERSION.privacy` in `src/config/policies.js` *and* the server's copy in
 * `functions/src/shared/config.js`, which asks every signed-in account to re-accept.
 *
 * The sections are declared as data so the contents list and the sections it links
 * to cannot drift apart; `src/ui/legal.js` renders them, and the terms of use are
 * built the same way.
 */

import { el } from "../ui/dom.js";
import {
  bullets,
  policyTable,
  prose,
  renderLegalDocument,
  subheading,
  supportLink,
} from "../ui/legal.js";

/** @type {import("../app/router.js").PageRenderer} */
export default function renderPrivacy() {
  return renderLegalDocument({
    title: "Privacy Policy",
    updated: "25 July 2026",
    slug: "privacy",
    sections: policySections(),
  });
}

/**
 * The policy itself, section by section.
 *
 * Built fresh on each render rather than held as module state, so a second render
 * cannot move nodes out of the first one's fragment.
 *
 * @returns {import("../ui/legal.js").LegalSection[]}
 */
function policySections() {
  return [
    {
      id: "short-version",
      title: "The short version",
      content: [
        prose(["KinCode asks for as little personal information as possible."]),
        prose([
          "If you create an account, we use your email address to let you sign in and manage your recovery profiles. Each profile contains a small number of contact first names and telephone numbers, protected by a four-word phrase and a separate six-digit PIN.",
        ]),
        prose([
          "Contact records are encrypted. The PIN is not stored in readable form, and the phrase is stored as a protected lookup value rather than as an openly searchable identifier.",
        ]),
        prose([
          "Someone using the public retrieval page does not need an account and is not asked for their name, email address, age or location. They enter the phrase and PIN to retrieve the saved contact names and numbers.",
        ]),
        prose(["We do not:"]),
        bullets([
          "sell personal information;",
          "use advertising;",
          "use personal information for profiling;",
          "show account holders who retrieved a profile or when it was retrieved;",
          "put the PIN, contact names or telephone numbers on a printed card or in a QR code.",
        ]),
        prose([
          "We use Stripe to process payments. We do not receive or store full payment-card details.",
        ]),
        prose(["Questions or privacy requests can be sent to ", supportLink(), "."]),
      ],
    },
    {
      id: "who-we-are",
      title: "1. Important information and who we are",
      content: [
        subheading("Purpose of this privacy policy"),
        prose([
          "This privacy policy explains how KinCode collects, uses and protects personal data when you:",
        ]),
        bullets([
          "visit the KinCode website;",
          "create or manage an account;",
          "create or use a recovery profile;",
          "retrieve saved contacts;",
          "purchase a subscription;",
          "contact us for support.",
        ]),
        prose([
          "In this policy, the KinCode website, application and related services are collectively called the ",
          el("strong", {}, ["Service"]),
          ".",
        ]),
        subheading("Controller"),
        prose(["DRMSITE LTD is the controller responsible for your personal data."]),
        el("dl", { class: "kin-details" }, [
          el("dt", {}, ["Company number"]),
          el("dd", {}, ["17151917"]),
          el("dt", {}, ["Registered office"]),
          el("dd", {}, ["40 Elm Close, Witchford, Ely, CB6 2JH"]),
          el("dt", {}, ["Email"]),
          el("dd", {}, [supportLink()]),
        ]),
        prose([
          "DRMSITE LTD is referred to as “KinCode”, “we”, “us” or “our” in this privacy policy.",
        ]),
      ],
    },
    {
      id: "how-kincode-works",
      title: "2. How KinCode works",
      content: [
        prose([
          "An adult account holder can create one or more recovery profiles. A profile contains a small number of trusted contact first names and telephone numbers.",
        ]),
        prose(["KinCode generates:"]),
        bullets(["a memorable four-word phrase; and", "a separate six-digit PIN."]),
        prose([
          "Anyone who has both the phrase and PIN can use the public retrieval page to reveal the contacts stored in that profile. We do not independently verify the identity of the person entering the credentials.",
        ]),
        prose(["The account holder is responsible for:"]),
        bullets([
          "sharing the phrase and PIN only with appropriate people;",
          "keeping the PIN separate from any printed card;",
          "keeping contact information accurate;",
          "ensuring they have an appropriate reason to store another person’s contact details;",
          "telling the people whose telephone numbers they add where appropriate.",
        ]),
        prose([
          "KinCode does not sell, print or post cards. The card you can print is produced by your own browser: it carries the profile label, the four-word phrase, a QR code and short instructions, and not the PIN.",
        ]),
        prose([
          "The QR code contains the four-word phrase and nothing else of yours — never the PIN, contact names or telephone numbers. Scanning it opens the retrieval page with the phrase already filled in; the PIN still has to be typed. The phrase travels in the part of the web address that browsers do not send to any server, so scanning a card does not record the phrase in our logs. It may still be recorded by the phone that scanned it or by the app that scanned it, which is outside our control.",
        ]),
        prose([
          "A QR code can be read by a camera at a distance or from a photograph, so treat a printed card as you would a spare key. If a card is lost, you can replace the phrase for that profile from your dashboard, which stops every card already printed for it from working.",
        ]),
      ],
    },
    {
      id: "data-we-collect",
      title: "3. The personal data we collect",
      content: [
        prose(["Personal data is information relating to an identified or identifiable person."]),
        subheading("Account Data"),
        prose(["Account Data may include:"]),
        bullets([
          "your email address;",
          "your Firebase account identifier;",
          "your email-verification status;",
          "account creation and update dates;",
          "authentication and account-security information;",
          "your accepted terms and privacy-policy version.",
        ]),
        prose([
          "Your password is handled by Firebase Authentication. We do not store or have access to your password in readable form.",
        ]),
        subheading("Recovery Profile Data"),
        prose(["Recovery Profile Data may include:"]),
        bullets([
          "an optional profile label chosen by the account holder;",
          "contact first names;",
          "contact telephone numbers;",
          "the system-generated phrase;",
          "a protected verifier derived from the PIN;",
          "profile status and creation or update dates;",
          "the order in which contacts should be displayed.",
        ]),
        prose([
          "The profile label, contact names and telephone numbers are stored in encrypted form.",
        ]),
        prose([
          "We do not need the full name, date of birth, address, photograph, school, workplace, health information or precise location of the person who will use the profile.",
        ]),
        prose(["Please do not place additional personal information in contact-name fields."]),
        subheading("Credential Data"),
        prose(["Credential Data includes:"]),
        bullets([
          "the four-word phrase;",
          "the protected phrase lookup value;",
          "a cryptographic verifier derived from the PIN;",
          "credential creation and reset dates.",
        ]),
        prose(["We do not store the PIN in readable form."]),
        prose([
          "When credentials are entered on the retrieval page, they are processed briefly so that we can verify them and return the correct contacts.",
        ]),
        subheading("Retrieval and Security Data"),
        prose([
          "When the public retrieval page or another part of KinCode is used, we may process limited technical information needed to operate and protect the Service, including:",
        ]),
        bullets([
          "internet protocol address;",
          "browser and device type;",
          "approximate country or region derived from an IP address;",
          "Firebase App Check tokens and the device or browser signals behind them, in the signed-in account area;",
          "request dates and times;",
          "failed-attempt counts;",
          "temporary rate-limit or abuse-prevention identifiers;",
          "application and server error information.",
        ]),
        prose([
          "We use this information to distinguish genuine use from automated attacks, prevent credential guessing, investigate faults and protect the Service.",
        ]),
        prose([
          "Firebase App Check uses Google reCAPTCHA Enterprise. It runs only in the signed-in account area. reCAPTCHA is not loaded on the public retrieval page, so retrieving contacts with a phrase and PIN does not involve reCAPTCHA.",
        ]),
        prose([
          "We do not provide individual retrieval histories to account holders. An account holder cannot use KinCode to see who retrieved a profile, where they were or when they accessed it.",
        ]),
        subheading("Billing Data"),
        prose([
          "If you purchase a subscription or another paid service, Billing Data may include:",
        ]),
        bullets([
          "your Stripe customer identifier;",
          "subscription and transaction identifiers;",
          "the product or plan purchased;",
          "payment status;",
          "renewal and cancellation dates;",
          "billing email address;",
          "invoice and refund information;",
          "the last four digits and type of a payment card where Stripe makes that information available to us.",
        ]),
        prose([
          "Stripe collects and processes payment details directly. We do not receive or store your complete card number, security code or bank-account credentials.",
        ]),
        subheading("Printed cards"),
        prose([
          "KinCode does not sell, print or deliver cards, so we do not process a name, delivery address, order reference or print artwork. The card you can print is produced by your own browser from information already shown on screen, and printing it sends nothing to us or to anyone else.",
        ]),
        prose([
          "If we introduce a printing and delivery service, we will update this policy before it becomes available and explain what the print provider receives. A card produced that way would still never carry the PIN, contact names or telephone numbers.",
        ]),
        subheading("Communications Data"),
        prose(["If you contact us, we may collect:"]),
        bullets([
          "your name;",
          "email address;",
          "the content of your message;",
          "related support or complaint records.",
        ]),
        prose([
          "Please do not send a PIN or unnecessary contact information in a support message.",
        ]),
        subheading("Aggregated information"),
        prose(["We may create anonymous or aggregated statistics about matters such as:"]),
        bullets([
          "the number of active accounts;",
          "the number of profiles created;",
          "subscription totals;",
          "general error rates;",
          "service capacity and performance.",
        ]),
        prose(["Information that cannot reasonably identify a person is not personal data."]),
      ],
    },
    {
      id: "other-people",
      title: "4. Information about other people",
      content: [
        prose([
          "An account holder may add the first name and telephone number of another person as a trusted contact.",
        ]),
        prose([
          "If your details are added, they may be revealed to someone who correctly enters the relevant four-word phrase and PIN. This is the purpose of the Service.",
        ]),
        prose(["We ask account holders to:"]),
        bullets([
          "add only people who may reasonably expect to be contacted;",
          "tell adult contacts that their details have been added where appropriate;",
          "obtain the agreement of another adult before creating a recovery profile specifically for that person;",
          "remove details that are no longer needed or accurate.",
        ]),
        prose([
          "Because contact details are encrypted and are not indexed by telephone number, we may need information from you and the relevant account holder to locate a record if you make a privacy request.",
        ]),
      ],
    },
    {
      id: "how-we-collect",
      title: "5. How we collect personal data",
      content: [
        prose(["We collect personal data through:"]),
        subheading("Information you provide"),
        prose(["You provide information when you:"]),
        bullets([
          "create an account;",
          "create or update a recovery profile;",
          "add or update contacts;",
          "subscribe to a paid plan;",
          "contact us;",
          "request account deletion or exercise a privacy right.",
        ]),
        subheading("Automated technologies"),
        prose([
          "Firebase and our backend systems automatically process limited technical and security information when you use KinCode. App Check, and the Google reCAPTCHA Enterprise check behind it, run only when you are signed in to an account.",
        ]),
        prose([
          "These measures may use cookies, browser storage, tokens or similar technologies where necessary to authenticate users and protect the Service.",
        ]),
        subheading("Payment providers"),
        prose([
          "Stripe sends us information about subscriptions, successful and failed payments, cancellations, invoices and refunds.",
        ]),
        prose([
          "Stripe may independently collect transaction, contact, device and fraud-prevention information when providing its payment services.",
        ]),
      ],
    },
    {
      id: "how-we-use",
      title: "6. How we use personal data and our legal bases",
      content: [
        prose(["We use personal data only where we have a lawful basis."]),
        policyTable(
          "Purposes, the data used and our legal basis",
          ["Purpose", "Data used", "Legal basis"],
          [
            [
              "Creating and managing an account",
              "Account Data",
              "Performance of our contract with the account holder; legitimate interests in operating secure accounts",
            ],
            [
              "Creating and managing recovery profiles",
              "Account, Recovery Profile and Credential Data",
              "Performance of our contract with the account holder",
            ],
            [
              "Storing details belonging to trusted contacts or another profile user",
              "Recovery Profile Data",
              "Legitimate interests in providing the contact-recovery service requested by the account holder, balanced against the rights of the people concerned",
            ],
            [
              "Retrieving contacts after valid credentials are supplied",
              "Credential, Recovery Profile and Security Data",
              "Performance of our contract; legitimate interests in providing and securing the Service",
            ],
            [
              "Preventing guessing, fraud, misuse and automated attacks",
              "Account, Credential, Retrieval and Security Data",
              "Legitimate interests in protecting users, contacts and KinCode; compliance with legal obligations where applicable",
            ],
            [
              "Processing subscriptions and payments",
              "Account and Billing Data",
              "Performance of our contract; compliance with accounting, tax and legal obligations",
            ],
            [
              "Sending verification, security, payment and service messages",
              "Account and Billing Data",
              "Performance of our contract; legitimate interests in administering the Service",
            ],
            [
              "Answering support requests and complaints",
              "Account and Communications Data",
              "Performance of our contract; legitimate interests in supporting users and resolving disputes",
            ],
            [
              "Testing, maintaining and improving reliability",
              "Technical, Security and aggregated data",
              "Legitimate interests in operating a safe, reliable and accessible Service",
            ],
            [
              "Establishing or defending legal claims",
              "Relevant account, billing, technical and communications information",
              "Legitimate interests; legal obligation",
            ],
          ],
        ),
        prose([
          "Where we rely on legitimate interests, we consider whether the processing is necessary and whether the rights and interests of the affected person override our interests. Particular care is required where a child’s information may be involved.",
        ]),
        subheading("Service communications"),
        prose(["We may send messages necessary to operate the Service, including:"]),
        bullets([
          "email-verification messages;",
          "password-reset messages;",
          "security notifications;",
          "payment receipts;",
          "renewal and failed-payment notices;",
          "notices about important changes to the Service, terms or privacy policy.",
        ]),
        prose(["These are service messages rather than marketing."]),
        subheading("Marketing"),
        prose([
          "We do not use contact records, retrieval activity or profile information for marketing.",
        ]),
        prose([
          "We will not send promotional email unless you have separately chosen to receive it. You can withdraw that choice at any time.",
        ]),
        subheading("No sale or third-party advertising"),
        prose(["We do not sell personal data."]),
        prose([
          "We do not share personal data with advertisers and do not use KinCode contact information for third-party marketing.",
        ]),
      ],
    },
    {
      id: "who-can-see",
      title: "7. Who can see personal data",
      content: [
        subheading("Account holders"),
        prose([
          "An authenticated account holder can see and manage the recovery profiles associated with their account.",
        ]),
        subheading("People using recovery credentials"),
        prose([
          "Anyone who supplies a valid phrase and PIN can see the contact first names and telephone numbers stored in that profile.",
        ]),
        prose(["They cannot see:"]),
        bullets([
          "the account holder’s email address;",
          "billing information;",
          "other recovery profiles;",
          "the profile’s PIN;",
          "information about previous retrievals.",
        ]),
        subheading("KinCode personnel"),
        prose([
          "Access to personal data is restricted to people who have a genuine operational need, such as responding to a support request, investigating misuse or maintaining the Service.",
        ]),
        subheading("Service providers"),
        prose(["We may share the minimum information necessary with providers including:"]),
        bullets([
          [
            el("strong", {}, ["Google/Firebase"]),
            ", for authentication, hosting, databases, backend functions, security controls and service operation;",
          ],
          [
            el("strong", {}, ["Stripe"]),
            ", for checkout, subscription billing, payment processing, fraud prevention, invoices and refunds;",
          ],
          "email or customer-support providers where needed to send service messages or answer enquiries;",
          "professional advisers, including accountants, lawyers and insurers;",
          "public authorities where disclosure is required by law.",
        ]),
        prose([
          "We may also disclose information as part of a sale, merger, restructuring or transfer of all or part of our business. Any new controller would be required to use personal data consistently with this policy and applicable law.",
        ]),
      ],
    },
    {
      id: "international-transfers",
      title: "8. International transfers and data location",
      content: [
        prose([
          "KinCode configures its encrypted recovery-profile database and backend processing in Google Cloud’s London region. Google lists ",
          el("code", {}, ["europe-west2"]),
          " as London for Firestore and Cloud Functions.",
        ]),
        prose(["Not every service used by KinCode is restricted to the UK:"]),
        bullets([
          "Firebase Authentication processes account-authentication data in the United States;",
          "some Firebase services operate on global Google infrastructure;",
          "Stripe and its service providers may process personal data internationally;",
          "another provider may process information outside the UK where necessary to provide its service.",
        ]),
        prose([
          "Google states that Firebase Authentication operates exclusively from US data centres. Stripe also states that personal data may be transferred to the United States and other countries in which its affiliates and subprocessors operate.",
        ]),
        prose([
          "Where personal data is transferred outside the UK, we use an appropriate safeguard where required, such as:",
        ]),
        bullets([
          "a UK adequacy regulation;",
          "the UK International Data Transfer Agreement;",
          "the UK Addendum to the European Commission’s standard contractual clauses;",
          "another transfer mechanism permitted by UK data-protection law.",
        ]),
      ],
    },
    {
      id: "security",
      title: "9. Data security",
      content: [
        prose([
          "KinCode uses technical and organisational measures intended to protect personal data.",
        ]),
        prose(["These include:"]),
        bullets([
          "encryption of contact names, telephone numbers and profile labels;",
          "protected lookup values for four-word phrases;",
          "cryptographic hashing of PINs;",
          "separation of phrases and PINs;",
          "authenticated backend functions;",
          "restrictions preventing the public browser from reading the database directly;",
          "Firebase App Check, using Google reCAPTCHA Enterprise, in the signed-in account area;",
          "rate limiting and progressive delays for repeated failed attempts;",
          "generic error messages that do not reveal whether a phrase exists;",
          "access controls for administrative systems;",
          "secure transport using HTTPS;",
          "log filtering intended to prevent credentials and contact details being recorded;",
          "procedures for handling suspected personal-data breaches.",
        ]),
        prose([
          "No online system can be guaranteed completely secure. Account holders should protect their account credentials and should not place the phrase and PIN together on the same printed card.",
        ]),
        prose([
          "If we become aware of a personal-data breach, we will investigate it and notify affected people and the Information Commissioner’s Office where the law requires us to do so.",
        ]),
      ],
    },
    {
      id: "retention",
      title: "10. Retention and deletion",
      content: [
        prose([
          "We retain personal data only for as long as reasonably necessary for the reason it was collected, including legal, security, accounting and reporting requirements.",
        ]),
        subheading("Account and recovery-profile information"),
        prose(["Account and Recovery Profile Data is retained while the account remains active."]),
        prose(["When an account holder deletes a recovery profile:"]),
        bullets([
          "the profile stops being retrievable;",
          "its active encrypted contact data is scheduled for deletion;",
          "limited records may remain temporarily in backups and security logs.",
        ]),
        prose([
          "When an account is deleted, active account and recovery-profile information is normally deleted within 30 days where technically practical.",
        ]),
        subheading("Backups and security information"),
        prose([
          "Backup copies are kept for a limited period. Point-in-time recovery of the database covers a rolling seven days, and any scheduled backup export is removed by a retention rule set at no more than 30 days.",
        ]),
        prose([
          "Records of account-administration events—such as a profile being created, credentials being reset or deletion being requested—are retained for up to 12 months. They hold event types, identifiers and counts only: no phrase, PIN, contact name or telephone number. They are also erased when the account is deleted.",
        ]),
        prose(["Rate-limit and failed-attempt records expire automatically within 24 hours."]),
        subheading("Billing records"),
        prose([
          "Subscription, payment, invoice and refund records may be retained for up to six years—or longer where required—to satisfy tax, accounting, fraud-prevention or legal obligations. UK limited companies are generally required to retain relevant accounting and company records for prescribed periods.",
        ]),
        subheading("Complaints and legal matters"),
        prose(["We may retain relevant information for longer where:"]),
        bullets([
          "a complaint remains open;",
          "misuse or fraud is being investigated;",
          "a legal claim has been made or is reasonably anticipated;",
          "the law requires us to keep it.",
        ]),
        prose([
          "Where possible, information retained for these purposes will be restricted and kept separately from active Service data.",
        ]),
      ],
    },
    {
      id: "your-rights",
      title: "11. Your legal rights",
      content: [
        prose(["Depending on the circumstances, you may have the right to:"]),
        bullets([
          [el("strong", {}, ["request access"]), " to personal data we hold about you;"],
          [el("strong", {}, ["request correction"]), " of incomplete or inaccurate information;"],
          [
            el("strong", {}, ["request erasure"]),
            " of personal data where there is no lawful reason for us to continue processing it;",
          ],
          [el("strong", {}, ["object to processing"]), " based on our legitimate interests;"],
          [el("strong", {}, ["request restriction"]), " of processing in certain circumstances;"],
          [
            el("strong", {}, ["request data portability"]),
            " for information you supplied to us where the right applies;",
          ],
          [el("strong", {}, ["withdraw consent"]), " where processing is based on consent;"],
          [el("strong", {}, ["complain to the Information Commissioner’s Office"]), "."],
        ]),
        prose([
          "Some rights are subject to exceptions. For example, we may need to retain billing information to meet legal and accounting obligations.",
        ]),
        prose([
          "The UK GDPR requires organisations to give people clear information about how their personal data is handled and the rights available to them.",
        ]),
        subheading("Accessing, correcting and deleting information"),
        prose([
          "Account holders can manage much of their information directly through KinCode, including:",
        ]),
        bullets([
          "updating contacts;",
          "resetting profile credentials;",
          "deleting recovery profiles;",
          "cancelling or managing a subscription;",
          "deleting their account;",
          "downloading their account data.",
        ]),
        prose(["To make another privacy request, contact ", supportLink(), "."]),
        subheading("Requests concerning a saved contact"),
        prose([
          "If your telephone number has been added to another person’s recovery profile, please provide enough information for us to locate the relevant record safely. We may need to contact the account holder or ask you for further information.",
        ]),
        prose([
          "We will not disclose another person’s account information merely because a telephone number appears in one of their encrypted profiles.",
        ]),
        subheading("Identity verification"),
        prose([
          "We may ask for information to confirm your identity before acting on a request. This is intended to prevent personal information being disclosed or deleted at the request of someone who is not entitled to act.",
        ]),
        subheading("Fees and response times"),
        prose(["You will not normally have to pay a fee to exercise your rights."]),
        prose([
          "We may charge a reasonable fee or refuse a request where it is clearly unfounded, repetitive or excessive.",
        ]),
        prose([
          "We aim to respond to legitimate requests within one month. If a request is complex or you have submitted several requests, we may take longer, but we will explain the delay.",
        ]),
      ],
    },
    {
      id: "children",
      title: "12. Children",
      content: [
        prose([
          "KinCode account registration and profile administration are intended for adults aged 18 or over.",
        ]),
        prose([
          "The public contact-retrieval feature may be used by children. A child does not need to create an account or provide a name, email address, date of birth, school, location or other profile information to retrieve contacts.",
        ]),
        prose([
          "Limited Technical and Security Data may still be processed to protect the retrieval service from guessing and automated abuse.",
        ]),
        prose(["KinCode is designed with high-privacy defaults for everyone. It does not:"]),
        bullets([
          "create child profiles for advertising or behavioural purposes;",
          "display advertising;",
          "profile children;",
          "use retrieval activity to monitor a child;",
          "tell an account holder when or where a profile was used;",
          "encourage children to provide additional personal information.",
        ]),
        prose([
          "The ICO’s Children’s Code can apply to online services likely to be accessed by people under 18 even when children are not the service’s sole or primary audience.",
        ]),
        prose([
          "If you believe a child has created an account or supplied unnecessary personal information, contact ",
          supportLink(),
          ".",
        ]),
      ],
    },
    {
      id: "cookies",
      title: "13. Cookies and browser storage",
      content: [
        prose([
          "KinCode uses limited cookies and similar technologies that are necessary to provide and protect the Service.",
        ]),
        prose(["These may include:"]),
        policyTable(
          "Cookies and similar technologies, their purpose and how long they last",
          ["Technology", "Purpose", "Typical duration"],
          [
            [
              "Firebase Authentication storage",
              "Keeps an account holder signed in",
              "Until sign-out, expiry or browser data is cleared",
            ],
            [
              "App Check and reCAPTCHA security data",
              "Helps distinguish genuine traffic from automated abuse in the signed-in account area; not used on the public retrieval page",
              "Set and controlled by Google according to the relevant security service",
            ],
            [
              "Session or local storage",
              "Remembers necessary interface or security state",
              "Session-only or until browser data is cleared",
            ],
            [
              "Stripe checkout storage",
              "Operates checkout, prevents fraud and processes payment",
              "Controlled by Stripe",
            ],
            [
              "Application cache",
              "Helps load the website and its static files efficiently",
              "Until updated or cleared",
            ],
          ],
        ),
        prose([
          "Firebase supports different forms of browser persistence for authentication sessions.",
        ]),
        prose(["We do not use advertising cookies or cross-site behavioural tracking."]),
        prose([
          "We do not currently use optional analytics cookies. If that changes, we will update this policy and request consent where required.",
        ]),
        prose([
          "Blocking necessary browser storage may prevent sign-in, checkout or other parts of KinCode from working correctly.",
        ]),
      ],
    },
    {
      id: "automated-decisions",
      title: "14. Automated security decisions",
      content: [
        prose([
          "KinCode uses automated security controls to detect suspicious activity, repeated failed credentials and automated traffic.",
        ]),
        prose(["These controls may:"]),
        bullets([
          "delay a request;",
          "require an additional security check;",
          "temporarily limit or block further attempts;",
          "treat traffic that does not pass App Check as suspicious when applying those limits.",
        ]),
        prose([
          "They are intended to protect profiles and do not make decisions that have legal or similarly significant effects on an individual.",
        ]),
        prose([
          "A failed App Check does not by itself prevent someone retrieving contacts. Retrieval is used in exactly the circumstances where a device may be unfamiliar or a check may fail, so the signal informs our rate limiting rather than acting as a gate.",
        ]),
        prose([
          "KinCode does not permanently lock a recovery profile solely because someone repeatedly enters an incorrect PIN.",
        ]),
      ],
    },
    {
      id: "third-party-links",
      title: "15. Third-party links",
      content: [
        prose([
          "KinCode may contain links to websites operated by other organisations, including Stripe or the Information Commissioner’s Office.",
        ]),
        prose([
          "Those organisations control their own websites and personal-data practices. Their privacy policies apply when you use their services.",
        ]),
      ],
    },
    {
      id: "complaints",
      title: "16. Complaints",
      content: [
        prose([
          "Please contact us first if you have a concern about how KinCode uses personal data:",
        ]),
        prose([el("strong", {}, ["Email:"]), " ", supportLink()]),
        prose([
          "You also have the right to complain to the Information Commissioner’s Office, the UK regulator for data-protection matters.",
        ]),
      ],
    },
    {
      id: "changes",
      title: "17. Changes to this privacy policy",
      content: [
        prose(["We keep this privacy policy under review."]),
        prose(["We may update it to reflect:"]),
        bullets([
          "changes to KinCode;",
          "new payment or print providers;",
          "security improvements;",
          "changes to our data-processing practices;",
          "changes in law or regulatory guidance.",
        ]),
        prose(["The date at the top shows when the policy was last updated."]),
        prose([
          "Where a change materially affects account holders, we will provide reasonable notice through KinCode or by email where appropriate.",
        ]),
        prose([
          "Please keep your account email address current so that you can receive important service and privacy notices.",
        ]),
      ],
    },
  ];
}
